import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Popover } from "antd";
import { CloseOutlined, RightOutlined } from "@ant-design/icons";
import { getColorCodeLecture } from "Pages/MyScheduleBeta/utils/unmarkedLectureHelper";

export interface MoreSessionsCardProps {
  event: any;
  gridStartHour?: number;
  hourHeight?: number;
  left?: string | number;
  right?: string | number;
  width?: string | number;
}

type PopoverPlacement =
  | "rightTop"
  | "rightBottom"
  | "leftTop"
  | "leftBottom"
  | "right"
  | "left";

const formatTime = (dateStr: string) => {
  const d = new Date(dateStr);
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
};

const MoreSessionsCard: React.FC<MoreSessionsCardProps> = ({
  event,
  gridStartHour = 7,
  hourHeight = 80,
  left,
  right,
  width,
}) => {
  const navigate = useNavigate();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverPlacement, setPopoverPlacement] =
    useState<PopoverPlacement>("rightTop");
  const cardRef = useRef<HTMLDivElement>(null);

  const start = new Date(event.startTime);
  const end = new Date(event.endTime);

  const startHour = start.getHours();
  const startMin = start.getMinutes();

  // compute duration in minutes
  const durationMins = (end.getTime() - start.getTime()) / (1000 * 60);

  // Math for positioning
  const topOffset = (startHour - gridStartHour + startMin / 60) * hourHeight;
  const height = (durationMins / 60) * hourHeight;

  const handleHiddenClick = (ev: any) => {
    setPopoverOpen(false);
    navigate(`/admin/schedule-beta/lecture-details/${ev.batchTimeTableId}`, {
      state: { lecture: ev, selectedSchedule: ev, event: ev },
    });
  };

  const calculatePlacement = (
    element?: HTMLElement | null
  ): PopoverPlacement => {
    const el = element || cardRef.current;
    if (!el) return "rightTop";

    const rect = el.getBoundingClientRect();
    const popoverWidth = 320;
    const popoverHeight = 350;
    const gap = 12;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const spaceOnRight = viewportWidth - rect.right;
    const spaceOnLeft = rect.left;
    const spaceBelow = viewportHeight - rect.top;
    const spaceAbove = rect.bottom;

    const isRight =
      spaceOnRight >= popoverWidth + gap || spaceOnRight >= spaceOnLeft;
    const isTop = spaceBelow >= popoverHeight + gap || spaceBelow >= spaceAbove;

    if (isRight && isTop) return "rightTop";
    if (isRight && !isTop) return "rightBottom";
    if (!isRight && isTop) return "leftTop";
    return "leftBottom";
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setPopoverPlacement(calculatePlacement(e.currentTarget));
    setPopoverOpen(true);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setPopoverPlacement(calculatePlacement());
      setPopoverOpen(true);
    } else {
      setPopoverOpen(false);
    }
  };

  const popoverContent = (
    <div>
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "#e1f5fe",
            color: "#0288d1",
            padding: "4px 12px",
            borderRadius: "16px",
            fontSize: "12px",
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          {event.hiddenEvents?.length || 0} MORE SESSIONS
        </div>
        <CloseOutlined
          style={{ cursor: "pointer", color: "#757575" }}
          onClick={(e) => {
            e.stopPropagation();
            setPopoverOpen(false);
          }}
        />
      </div>
      <div style={{ padding: "8px 0", maxHeight: "350px", overflowY: "auto" }}>
        <div
          style={{
            padding: "8px 16px",
            color: "#5f6368",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          {new Date(event.startTime).toLocaleDateString("en-GB", {
            weekday: "short",
            day: "numeric",
            month: "short",
          })}
        </div>
        {event.hiddenEvents?.map((ev: any) => (
          <div
            key={ev.batchTimeTableId}
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #f0f0f0",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleHiddenClick(ev);
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#212121",
                }}
              >
                {ev.eventName} {ev.subject ? `· ${ev.subject}` : ""}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#5f6368",
                }}
              >
                {ev.batchName}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#5f6368",
                  marginTop: "4px",
                }}
              >
                {formatTime(ev.startTime)} · {ev.room || "Room"}
              </div>
            </div>
            <RightOutlined style={{ color: "#bdbdbd" }} />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={popoverOpen}
      onOpenChange={handleOpenChange}
      placement={popoverPlacement}
      autoAdjustOverflow={true}
      getPopupContainer={() => document.body}
      overlayInnerStyle={{
        padding: 0,
        borderRadius: "12px",
        overflow: "hidden",
        width: "300px",
        maxWidth: "calc(100vw - 24px)",
        boxSizing: "border-box",
      }}
    >
      <div
        ref={cardRef}
        style={{
          position: "absolute",
          top: `${topOffset}px`,
          height: `${height}px`,
          left:
            left !== undefined
              ? typeof left === "number"
                ? `${left}px`
                : left
              : "8px",
          right:
            width !== undefined
              ? undefined
              : right !== undefined
              ? typeof right === "number"
                ? `${right}px`
                : right
              : "16px",
          width:
            width !== undefined
              ? typeof width === "number"
                ? `${width}px`
                : width
              : undefined,
          backgroundColor: "#e8eaed",
          borderRadius: "8px",
          padding: "8px 12px",
          cursor: "pointer",
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "1px solid",
          borderColor: getColorCodeLecture(event.syllabusStatus),
        }}
        onClick={handleCardClick}
      >
        <div
          style={{
            fontSize: "16px",
            fontWeight: 600,
            color: getColorCodeLecture(event.syllabusStatus),
          }}
        >
          +{event.hiddenEvents?.length || 0}
        </div>
      </div>
    </Popover>
  );
};

export default MoreSessionsCard;
